import { Injectable, inject } from '@angular/core';

import { FiltersConfig, HttpCoreService, IFilterQueryResponse } from '@dataclouder/ngx-core';

import { ILesson, ILessonTaken, LessonPrompts, LessonsAbstractService } from '../components/lesson-mini-components/components/lessons.clases';

// Define placeholder endpoints - these should be configured appropriately
const LESSONS_BASE_PATH = 'api/lesson'; // Example base path

@Injectable({
  providedIn: 'root',
})
export class DefaultLessonsService implements LessonsAbstractService {
  saveTakenLesson(lesson: ILessonTaken): Promise<any> {
    // Not sure how to implement this yet.

    return this.httpCoreService.post(this.endpoints.saveLesson, lesson) as Promise<any>;
  }
  private httpCoreService = inject(HttpCoreService);

  // --- Endpoint Definitions (Hardcoded as requested) ---
  private endpoints = {
    queryLessons: `${LESSONS_BASE_PATH}/query`,
    getLesson: (id: string) => `${LESSONS_BASE_PATH}/${id}`,
    saveLesson: `api/user/saveLesson`,
    updateLesson: (id: string) => `${LESSONS_BASE_PATH}/${id}`, // Assuming PUT to /lessons/:id
    deleteLesson: (id: string) => `${LESSONS_BASE_PATH}/${id}`, // Assuming DELETE to /lessons/:id
    generateLesson: `${LESSONS_BASE_PATH}/generate`, // Placeholder
    generateByAI: `${LESSONS_BASE_PATH}/generate-ai`, // Placeholder
    improveMDWithAI: `${LESSONS_BASE_PATH}/improve-markdown-ai`, // Placeholder

    QueryLessons: 'api/lesson/query',
    Lesson: 'api/lesson',
    SaveLesson: 'api/lesson-polilan',
    GetPublicLessons: 'api/lesson/publicLessons',
    GetUnpublishedLessons: 'api/lesson/unpublished',
    TakenLesson: 'api/lesson/taken',
    DeleteLesson: 'api/lesson',
    Base: 'api/lesson',
    GenerateBanner: 'api/lesson/generate-banner',
  };

  // --- Method Implementations ---

  async getLessons(filters: FiltersConfig = {}): Promise<IFilterQueryResponse<ILesson>> {
    // Return only basic properties for lessons, not the content
    if (!filters.returnProps) {
      filters.returnProps = {
        level: 1,
        title: 1,
        description: 1,
        createdDate: 1,
        banner: 1,
        metadata: 1,
        appExtensions: 1,
        media: 1,
      };
    }

    // Assuming paginator is the body for a POST request based on the example
    const response = await this.httpCoreService.post<IFilterQueryResponse<ILesson>>(this.endpoints.queryLessons, filters);

    if (response.rows) {
      // this.learningSourceCoreService.bulkUpsertKnowledgeCoreData('lessons', response.rows);
    }
    return response;
  }

  async getLesson(id: string): Promise<any> {
    return this.httpCoreService.get(this.endpoints.getLesson(id)) as Promise<any>;
  }

  async postLesson(lesson: ILesson): Promise<ILesson> {
    return this.httpCoreService.post<ILesson>(this.endpoints.saveLesson, lesson);
  }

  async updateLesson(lesson: ILesson): Promise<ILesson> {
    if (!lesson._id) {
      throw new Error('Lesson ID is required for update.');
    }
    // Assuming _id is the identifier
    return this.httpCoreService.put<ILesson>(this.endpoints.updateLesson(lesson._id), lesson);
  }

  async deleteLesson(id: string): Promise<ILesson> {
    return this.httpCoreService.delete<ILesson>(this.endpoints.deleteLesson(id));
  }

  async generateLesson(lesson: ILesson): Promise<ILesson> {
    // This endpoint might need specific data or structure
    return this.httpCoreService.post<ILesson>(this.endpoints.generateLesson, lesson);
  }

  async postGenerateByAI(id: string): Promise<ILesson> {
    return this.httpCoreService.post<ILesson>(this.endpoints.generateByAI, { id });
  }

  async postImproveMDWithAI(lessonId: string, markdownText: string): Promise<ILesson> {
    return this.httpCoreService.post<ILesson>(this.endpoints.improveMDWithAI, { id: lessonId, markdown: markdownText });
  }

  extractTextFromHtml(html: string): string {
    // Copied from src/app/core/data-services/lessons.service.ts
    const r1 = new RegExp('~(.+?)~', 'g');
    const lessonHtml = html.replace(r1, (_matching, jsonCoded) => {
      try {
        const data = JSON.parse(jsonCoded);
        return `<span>${data?.settings?.text || ''}</span>`; // Added default empty string
      } catch (e) {
        console.error('Error parsing JSON in extractTextFromHtml:', jsonCoded, e);
        return ''; // Return empty string on error
      }
    });

    // Remove HTML tags
    let text = lessonHtml.replace(/<[^>]*>/g, ' ');

    // Remove style and script content
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');

    // Decode HTML entities
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&/g, '&');
    text = text.replace(/</g, '<');
    text = text.replace(/>/g, '>');

    // Remove extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  }

  public generateBanner(prompt: string, lessonId: string): Promise<IFilterQueryResponse<ILesson>> {
    return this.httpCoreService.post<IFilterQueryResponse<ILesson>>(this.endpoints.GenerateBanner, { prompt, lessonId });
  }

  getPrompts(): LessonPrompts {
    return null;
  }
}
